import { Dispatch, SetStateAction, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import { CloseOutlined } from '@ant-design/icons'
import { Button, Typography } from 'antd'
import useSWR from 'swr'
import { BuildingFile } from 'types/building'

import Spinner from 'components/atoms/Spinner'
import LeftArrowIcon from 'components/icons/LeftArrowIcon'

import ViewerImage from './ViewerImage'
import ViewerPdf from './ViewerPdf'
import ViewerVideo from './ViewerVideo'

type Props = {
  fileIndex: number | undefined
  file: any
  maxLength: number
  setPreviewFileIndex: Dispatch<SetStateAction<number | undefined>> | undefined
}

export default function ViewerFilePreview({
  fileIndex,
  file,
  maxLength,
  setPreviewFileIndex
}: Props) {
  const videoTypes = 'mp4,mov'
  const imageTypes = 'jpg,png,jpeg'
  const pdfType = 'pdf'

  const fileExtension = file.extension.toLowerCase()

  const [searchParams, setSearchParams] = useSearchParams()

  const previewIndex = searchParams.get('preview_idx')

  // get file s3path on every render
  const { data: fileDetail, isValidating } = useSWR<BuildingFile>({
    url: `/v1/buildings/files/${file.id}`
  })

  const handleClosePreview = () => {
    if (fileIndex !== undefined && setPreviewFileIndex) {
      setPreviewFileIndex(undefined)
      return
    }
    searchParams.delete('preview_idx')
    setSearchParams(searchParams)
  }

  const isAtStartEdges = useMemo(() => {
    if (fileIndex !== undefined) {
      return fileIndex !== 0
    }
    return Boolean(previewIndex && +previewIndex !== 0)
  }, [fileIndex, previewIndex])

  const isAtEndEdges = useMemo(() => {
    if (fileIndex !== undefined) {
      return fileIndex !== maxLength - 1
    }
    return Boolean(previewIndex && +previewIndex !== maxLength - 1)
  }, [fileIndex, maxLength, previewIndex])

  const moveToNextFile = () => {
    if (fileIndex !== undefined && setPreviewFileIndex) {
      fileIndex < maxLength - 1 && setPreviewFileIndex((prev: any) => prev + 1)
      return
    }
    if (previewIndex && +previewIndex < maxLength - 1) {
      searchParams.set('preview_idx', `${+previewIndex + 1}`)
      setSearchParams(searchParams, { replace: true })
    }
  }

  const moveToPreviousFile = () => {
    if (fileIndex !== undefined && setPreviewFileIndex) {
      fileIndex > 0 && setPreviewFileIndex((prev: any) => prev - 1)
      return
    }
    if (previewIndex && +previewIndex > 0) {
      searchParams.set('preview_idx', `${+previewIndex - 1}`)
      setSearchParams(searchParams, { replace: true })
    }
  }

  if (pdfType.includes(fileExtension)) {
    if (!fileDetail || isValidating) {
      return isValidating ? <Spinner /> : null
    }
    return <ViewerPdf file={fileDetail} handleCloseFile={handleClosePreview} />
  }

  const content = (
    <div className="fixed inset-0 z-[99999] bg-[#000000CC] grid grid-rows-[auto_1fr] overflow-auto">
      <div className="sticky top-0 w-full h-[50px] grid grid-cols-[auto_1fr_auto] items-center gap-[10px] px-5 mb-[-50px] z-20">
        <div className="cursor-pointer" onClick={handleClosePreview}>
          <LeftArrowIcon color="#ffffff" size={24} />
        </div>
        <div
          className="truncate"
          style={{
            textShadow: '2px 2px #000000'
          }}
        >
          <Typography.Text
            className="font-bold text-white"
            ellipsis={{ tooltip: file.name }}
          >
            {file.name}
          </Typography.Text>
        </div>
        <Button
          className="rounded-full bg-transparent"
          onClick={handleClosePreview}
          icon={
            <CloseOutlined style={{ fontSize: '24px', color: '#FFFFFF' }} />
          }
        />
      </div>

      {fileDetail && !isValidating && (
        <>
          {videoTypes.includes(fileExtension) && (
            <div className="mt-[50px] pt-5 pb-10 flex items-center overflow-auto">
              <ViewerVideo file={fileDetail} />
            </div>
          )}
          {imageTypes.includes(fileExtension) && (
            <div className="m-full h-full bg-transparent z-10">
              <ViewerImage file={fileDetail} />
            </div>
          )}
        </>
      )}

      {!fileDetail && !isValidating && (
        <div className="text-white m-auto">ファイルが見つかりません</div>
      )}

      {isValidating && <Spinner />}

      {isAtStartEdges && (
        <div
          className="absolute top-1/2 -translate-y-1/2 left-7 w-[60px] h-[60px] bg-[#00000099] rounded-full grid place-items-center cursor-pointer z-20"
          onClick={moveToPreviousFile}
        >
          <LeftArrowIcon size={32} />
        </div>
      )}

      {isAtEndEdges && (
        <div
          className="absolute top-1/2 -translate-y-1/2 right-7 w-[60px] h-[60px] bg-[#00000099] rounded-full grid place-items-center rotate-180 cursor-pointer z-20"
          onClick={moveToNextFile}
        >
          <LeftArrowIcon size={32} />
        </div>
      )}
    </div>
  )

  return createPortal(content, document.body)
}
